import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import TableCell from '@material-ui/core/TableCell';
import classNames from 'classnames';

import { RootState } from '../../../../configs/store';
import formatOdds from '../../../../utils/formatOdds';
import Outcome from '../Outcome/Outcome';
import useOnScreen from 'src/hooks/useOnScreen';

interface EventRowMarketProps {
  mainMarket: any,
  event: {
    eventCode: number,
    mainMarkets: { typeCode: any, outcomesCount: any, isSuspended: string, lookupId: number }
    statusNumber: number
  },
  removeFromBetslip?: (index: number) => void, // eslint-disable-line
  addToBetslip?: (obj: {}, {}) => void, // eslint-disable-line
}

function EventRowMarket({ mainMarket, event, addToBetslip, removeFromBetslip }: EventRowMarketProps) {
  const { selectedOddsType } = useSelector((state: RootState) => state.odds);
  const betslipBets = useSelector((state: RootState) => state.betslip.bets);
  const refOdds = useRef();
  const bet = useSelector((state: RootState) => _.filter(state.betslip.bets, {
    eventCode: event.eventCode
  }));
  const liveEvents = useSelector((state:RootState) => state.events.liveOutcomes);
  const isVisible = useOnScreen(refOdds, '0px');

  const betIndex = useSelector((state: RootState) => _.findIndex(state.betslip.bets, {
    eventCode: event.eventCode
  }));
  const dispatch = useDispatch();

  const width = useMemo(() => 144 / (mainMarket.outcomes + (mainMarket.vars > 0 ? 1 : 0)), [mainMarket.outcomes, mainMarket.vars]);
  const percent = useMemo(() => mainMarket.vars > 0 && (width / 100) * 20, [mainMarket.vars, width]);
  const mainMarketsData = useMemo(() => new Array(mainMarket.outcomes + mainMarket.vars), [mainMarket.outcomes, mainMarket.vars]);
  const market = useMemo(
    () => _.find(event.mainMarkets, m => m.typeCode === mainMarket.marketTypeCode || m.typeCode === mainMarket.marketTypeCode)
    , [event.mainMarkets, mainMarket.marketTypeCode]
  );

  const handleOutcomeClick = useCallback((index, market, k, o, isSelected) => {
    const marketIndexToRemove = _.findIndex(betslipBets, { eventCode: event.eventCode, odds: o });

    index >=0 && isSelected
      ? removeFromBetslip(marketIndexToRemove)
      : addToBetslip(market, { value: o, code: market.outcomeCode[k], name: market.outcomeHdr[k] });
  }, [betslipBets, bet, addToBetslip, removeFromBetslip]);

  const marketSuspended = _.find(event.mainMarkets, { lookupId: mainMarket.marketLookUpCode })?.isSuspended;

  useEffect(() => {
    if (market && isVisible && market && market.outcomeCode && !(liveEvents.includes(market?.outcomeCode[0]))){
      dispatch.events.addEventToLiveOutcomes(market.outcomeCode);
    } else if (market && !isVisible && market && market?.outcomeCode && liveEvents.includes(market?.outcomeCode[0])){
      dispatch.events.removeFromLiveOutcomes(market.outcomeCode);
    }
  }, [isVisible]);

  if (!market) {
    return (
      <>
        {
          _.map(mainMarketsData, (empty, emptyIndex) => (
            <TableCell
              key={ emptyIndex }
              align="center"
              style={ { width: `${ width - (percent ? percent : 0) }px` } }
            >
              <div className="event-row__event-odds event-row__event-odds--empty"></div>
            </TableCell>
          ))
        }
        {
          mainMarket.outcomes <= 3 && <TableCell align="center" className="sport-table__divider"></TableCell>
        }
      </>
    );
  }

  const specifierValue = 'specifiers' in market && market.specifiers.length > 0 && market.specifiers[0].value;
  const outcomeOdds = mainMarket.outcomes || 0;
  const cellWidth = 144 / (outcomeOdds + (mainMarket.vars !== 0 && specifierValue ? 1 : 0));
  const twentyPercent = mainMarket.vars !== 0 && specifierValue && (cellWidth / 100) * 20;
  const headersCount = market.outcomesCount + (specifierValue ? 1 : 0);
  const diffHeadersOutcomes = mainMarket.outcomes - (market.outcomeOdds?.length || 0);

  return (
    <>
      {
        mainMarket.vars !== 0 && specifierValue && (
          <TableCell
            align="center"
            style={ { width: `${ cellWidth - twentyPercent }px` } }
          >
            <div
              className={
                classNames('event-row__event-odds event-row__event-odds--specifier', {
                  'event-row__event-odds--specifier-right-border': diffHeadersOutcomes > 0
                })
              }
            >
              { specifierValue }
            </div>
          </TableCell>
        )
      }

      {
        market.outcomesCount <= headersCount && _.map(market.outcomeOdds, (o, k: number) => {

          const mainMarkets = _.find(event.mainMarkets, { typeCode: mainMarket.marketTypeCode });
          const isSuspended = mainMarkets.isSuspended === 'true' || market.outcomeIsSuspended[k] || marketSuspended || event.statusNumber === 7;
          const marketIndex = _.findIndex(bet, { marketCode: market.marketCode, marketOutcomeCode: market.outcomeCode[k] });
          const isSelected = marketIndex >=0;

          return (
            <TableCell
              key={ k }
              align="center"
              ref= { refOdds }
              style={ { width: `${ cellWidth + (twentyPercent ? (twentyPercent / outcomeOdds) : 0) }px` } }
              className={
                classNames({
                  'event-row__event-odds--left': market.outcomesCount < 3 || k === 2,
                  'event-row__event-odds--first': k === 0 && mainMarket.vars === 0
                })
              }
            >
              <Outcome
                el="div"
                className={
                  classNames({
                    'event-row__event-odds': true,
                    'event-row__event-odds--selected': isSelected && betIndex >= 0,
                    'event-row__event-odds--right': k === market.outcomesCount - 1,
                    'event-row__event-odds--suspended': isSuspended,
                    'event-row__event-odds--increased': market.oddsChanged && market.oddsChanged[k] === 'increased',
                    'event-row__event-odds--decreased': market.oddsChanged && market.oddsChanged[k] === 'decreased'
                  })
                }
                onClick={ () => handleOutcomeClick(betIndex, market, k, o, isSelected) }
                value={ formatOdds(o, selectedOddsType) }
                isSuspended={ isSuspended }
              />
            </TableCell>
          );
        })
      }

      {
        _.map(new Array(diffHeadersOutcomes), (empty, emptyIndex) => (
          <TableCell
            key={ emptyIndex }
            align="center"
            style={ { width: `${ cellWidth + (twentyPercent ? (twentyPercent / outcomeOdds) : 0) }px` } }
          >
            <div className="event-row__event-odds event-row__event-odds--empty"></div>
          </TableCell>
        ))
      }

      {
        market.outcomesCount <= 3 && <TableCell align="center" className="sport-table__divider"></TableCell>
      }
    </>
  );
}

export default React.memo(EventRowMarket);
